import { Link } from "react-router-dom";
import { CheckCircle2, Target, Eye, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";

const REASONS = [
  { t: "Tailored Solutions", d: "Every business is unique, so we design strategies that fit your goals, not generic templates." },
  { t: "Proven Expertise", d: "Our team of marketers, strategists, and IT consultants bring years of experience across industries." },
  { t: "Creative Excellence", d: "Our designers bring fresh ideas and artistic flair to every project." },
  { t: "End-to-End Solutions", d: "From concept to launch, we handle every detail with precision and passion." },
  { t: "Comprehensive Services", d: "Security, IT, and marketing under one roof." },
  { t: "Expert Team", d: "Skilled professionals with a passion for innovation and excellence." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo title="About Us" description="Established in 2018, Digi Hub Solution is a trusted partner in digital marketing and IT consulting, based in Rishikesh, Uttarakhand." />
      {/* Header */}
      <section className="hero-gradient text-white">
        <div className="container-x py-20 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="chip-light">About Us</div>
            <h1 className="mt-4 text-4xl md:text-6xl">Empowering businesses in the <span className="text-[color:var(--dh-orange)]">digital age.</span></h1>
            <p className="mt-5 text-white/80 max-w-xl leading-relaxed">
              Established in 2018, Digi Hub Solution is a trusted partner in digital marketing and IT consulting. We combine creativity with technology to deliver solutions that drive measurable growth.
            </p>
          </div>
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1400&auto=format&fit=crop"
              alt="team" className="rounded-3xl border border-white/15 shadow-2xl w-full h-[380px] object-cover" />
          </div>
        </div>
      </section>

      {/* Intro paragraphs */}
      <section className="container-x py-20 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2 space-y-5 text-[color:var(--dh-slate)] leading-relaxed">
          <p>At Digi Hub Solution, we empower businesses to thrive in the digital age. As a trusted partner in digital marketing and IT consulting, we combine creativity with technology to deliver solutions that drive measurable growth.</p>
          <p>We believe that technology and creativity go hand in hand to build secure, connected, and thriving businesses. From safeguarding your premises with CCTV surveillance solutions to elevating your brand through digital marketing and design, we are your all-in-one partner for growth and security.</p>
          <p>At Digi Hub Solution, design is more than just visuals — it's how we tell your story. We specialise in graphic design and website design, blending creativity with functionality to help brands stand out and connect with their audiences.</p>
          <p>We provide cutting-edge IT business consulting services designed to drive innovation, efficiency and growth for your business. Our expert team helps you optimise technology, enhance operational efficiency and stay ahead of the competition.</p>
        </div>
        <div className="rounded-3xl bg-[color:var(--dh-cream)] p-8 border border-[color:var(--dh-line)]">
          <div className="eyebrow">Since 2018</div>
          <div className="mt-3 font-display text-4xl text-[color:var(--dh-navy)]">6+ Years</div>
          <p className="text-[color:var(--dh-slate)] mt-2 text-sm">of shipping designs, campaigns and IT projects that our clients still use every day.</p>
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div><div className="font-display text-2xl text-[color:var(--dh-orange)]">120+</div><div className="text-xs text-[color:var(--dh-slate)]">Projects</div></div>
            <div><div className="font-display text-2xl text-[color:var(--dh-orange)]">45+</div><div className="text-xs text-[color:var(--dh-slate)]">Clients</div></div>
          </div>
        </div>
      </section>

      {/* Mission + Vision */}
      <section className="container-x pb-20 grid md:grid-cols-2 gap-6">
        {[
          { i: Target, h: "Our Mission", t: "At Digi Hub Solutions, our mission is to help organisations unlock their full potential by bridging the gap between marketing innovation and IT excellence. We transform ideas into impactful designs that inspire, engage and deliver results, and provide IT consulting that sets a strong foundation for future growth." },
          { i: Eye, h: "Our Vision", t: "To be the go-to partner for businesses seeking growth through digital innovation and reliable IT solutions — empowering businesses with secure technology and powerful branding, so they can grow confidently in the digital era." },
        ].map(({ i: Icon, h, t }) => (
          <div key={h} className="rounded-3xl border border-[color:var(--dh-line)] p-8 bg-white">
            <div className="w-12 h-12 rounded-xl bg-[color:var(--dh-navy)] text-white grid place-items-center"><Icon className="w-5 h-5" /></div>
            <h3 className="mt-4 font-display text-2xl text-[color:var(--dh-navy)]">{h}</h3>
            <p className="mt-3 text-[color:var(--dh-slate)] leading-relaxed">{t}</p>
          </div>
        ))}
      </section>

      {/* Why choose us */}
      <section className="bg-[color:var(--dh-cream)] py-20">
        <div className="container-x">
          <div className="text-center">
            <div className="eyebrow justify-center">Why Choose Us</div>
            <h2 className="mt-3 text-3xl md:text-5xl text-[color:var(--dh-navy)]">Reasons clients stay with us</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {REASONS.map((r) => (
              <div key={r.t} className="p-6 rounded-2xl bg-white border border-[color:var(--dh-line)] hover:border-[color:var(--dh-orange)] transition-colors" data-testid={`about-reason-${r.t.split(' ')[0].toLowerCase()}`}>
                <CheckCircle2 className="w-6 h-6 text-[color:var(--dh-orange)]" />
                <div className="mt-3 font-display text-lg text-[color:var(--dh-navy)]">{r.t}</div>
                <p className="mt-1 text-sm text-[color:var(--dh-slate)]">{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-16 text-center">
        <h2 className="text-3xl md:text-4xl text-[color:var(--dh-navy)]">Let's build something remarkable.</h2>
        <p className="text-[color:var(--dh-slate)] mt-3">Tell us about your project — we'll respond within one business day.</p>
        <Link to="/contact" className="btn-primary mt-6">Start a Project <ArrowRight className="w-4 h-4" /></Link>
      </section>
    </div>
  );
}
